<template>
    <div class="autoprofile2" :id="id" :class="containerClasses">
        <div class="image-upload" :class="containerClasses">
            <input
                ref="file"
                type="file"
                style="display: none"
                accept="image/*"
                @change="selectFile"
            />
            <div 
                class="image-preview-wrapper" 
                :class="[imageWrapperClasses, { 'error': isError }]"
                @click="$refs.file.click()"
            >
                <div
                    v-if="!hasImage && !loading"
                    class="image-preview-placeholder"
                >
                    <img
                        src="@/assets/customizer_icon_upload.svg"
                        width="24"
                        height="24"
                    />
                    {{ texts.uploadButton }}
                </div>

                <div v-if="loading" class="loading-container">
                    <LoadingIndicator />
                </div>

                <img
                    v-if="hasImage"
                    :src="imagePreview"
                    :alt="imagePreviewAlt"
                />
            </div>
            <p
                @click="openImageUpload()"
                style="
                    font-size: 14px;
                    text-decoration: underline;
                    cursor: pointer;
                "
            >
                {{ texts.helpLink }}
            </p>
        </div>
    </div>
    
    <PopUp id="autoprofile2-popup" ref="AutoProfileAI2Popup" maxWidth="480px">
        <h3>{{ texts.popupTitle }}</h3>
        <p style="margin-top: 32px">
            {{ texts.recommendationsTitle }}
        </p>
        <ul style="padding-left: 25px">
            <li v-for="(recommendation, index) in recommendationsList" :key="index">
                {{ recommendation }}
            </li>
        </ul>
        <p style="margin-top: 16px; color: #b55e2b">
            {{ texts.privacy }}
        </p>
        <DTButton
            @click="
                $refs.file.click();
                $refs.AutoProfileAI2Popup.closePopup();
            "
            type="primary"
            :text="texts.uploadButton"
            style="margin-top: 32px; width: 100%"
        />
    </PopUp>
    
    <BottomSheet
        id="autoprofile2-bottomsheet"
        ref="AutoProfileAI2Bottomsheet"
        :title="texts.popupTitle"
    >
        <p style="margin: 24px 0 0">
            {{ texts.recommendationsTitle }}
        </p>
        <ul style="padding-left: 25px; margin: 0">
            <li v-for="(recommendation, index) in recommendationsList" :key="index">
                {{ recommendation }}
            </li>
        </ul>
        <p style="margin: 18px 0 0; color: #b55e2b">
            {{ texts.privacy }}
        </p>
        <DTButton
            @click="
                $refs.AutoProfileAI2Bottomsheet.closeBottomsheet();
                $refs.file.click();
            "
            type="primary"
            :text="texts.uploadButton"
            style="margin: 24px 0 0; width: 100%"
        />
    </BottomSheet>
    
    <MessageBox 
        id="autoprofile2-error-message" 
        :visible="false"
        type="error"
        :title="texts.errorTitle"
        :message="uploadErrorMessage || texts.errorMessage"
        margin="16px 0 0"
    />
</template>

<script>
import LoadingIndicator from "./LoadingIndicator.vue";
import PopUp from "./PopUp.vue";
import DTButton from "./DTButton.vue";
import BottomSheet from "./BottomSheet.vue";
import MessageBox from "./MessageBox.vue";
import axios from "axios";
import EventBus from "../event-bus";
import { inject, nextTick } from "vue";
import { safeGetJson, safeSetJson } from "../utils/safeStorage";
import { recordImageUpload } from "../utils/previewService";
import { prepareImageForUpload, waitForRelease } from "../utils/imageUploadPreparation";
import { captureCustomizerMessage } from "../utils/sentry";
import { builderText, customizerText } from "../utils/customizerTexts";

export default {
    name: "AutoProfileAI2",
    components: {
        LoadingIndicator,
        PopUp,
        DTButton,
        BottomSheet,
        MessageBox,
    },
    props: {
        id: {
            type: String,
            default: "",
        },
        required: {
            type: Boolean,
            default: false,
        },
        uploadButtonText: {
            type: String,
            default: "",
        },
        imagePayload: {
            type: String,
            required: true,
        },
        sharePayload: {
            type: Boolean,
            default: false,
        },
        popupTitle: {
            type: String,
            default: "",
        },
        recommendationsTitle: {
            type: String,
            default: "",
        },
        recommendations: {
            type: String,
            default: "",
        },
        privacyText: {
            type: String,
            default: "",
        },
        mediaLibraryText: {
            type: String,
            default: "",
        },
        cameraText: {
            type: String,
            default: "",
        },
        helpLinkText: {
            type: String,
            default: "",
        },
        webhookUrl: {
            type: String,
            required: true,
        },
        errorTitle: {
            type: String,
            default: "",
        },
        errorMessage: {
            type: String,
            default: "",
        },
        // Optional, per market: empty falls back to errorMessage.
        errorMessageTooLarge: {
            type: String,
            default: "",
        },
        errorMessageInvalidFile: {
            type: String,
            default: "",
        },
    },
    computed: {
        texts() {
            return {
                uploadButton: builderText(this.uploadButtonText, "addImageOptional"),
                popupTitle: builderText(this.popupTitle, "imageUploadTitle"),
                recommendationsTitle: builderText(this.recommendationsTitle, "recommendationsTitle"),
                privacy: builderText(this.privacyText, "privacy"),
                helpLink: builderText(this.helpLinkText, "howItWorks"),
                errorTitle: builderText(this.errorTitle, "uploadErrorTitle"),
                errorMessage: builderText(this.errorMessage, "uploadErrorMessage"),
            };
        },
        imagePreviewAlt() {
            return customizerText("imagePreviewAlt");
        },
        recommendationsList() {
            return builderText(this.recommendations, "recommendations").split(';').filter(item => item.trim() !== '');
        },
        hasImage() {
            return this.imagePreview && !this.loading;
        },
        isLandscape() {
            return this.imageAspectRatio > 1.1;
        },
        isPortrait() {
            return this.imageAspectRatio < 0.9;
        },
        isSquare() {
            return this.imageAspectRatio >= 0.9 && this.imageAspectRatio <= 1.1;
        },
        containerClasses() {
            return {
                'has-landscape': this.hasImage && this.isLandscape
            };
        },
        imageWrapperClasses() {
            return {
                'has-image': this.hasImage,
                'is-loading': this.loading,
                'is-portrait': this.isPortrait,
                'is-landscape': this.isLandscape,
                'is-square': this.isSquare
            };
        },
    },
    setup() {
        const pageSettings = inject("pageSettings", {});
        return { pageSettings };
    },
    data() {
        return {
            selectedFiles: undefined,
            loading: false,
            imagePreview: undefined,
            imageAspectRatio: 1,
            internalRequired: this.required,
            isError: false,
            uploadedImageUrl: "",
            uploadErrorMessage: "",
            uploadAttempt: 0
        };
    },
    created() {
        // Not reactive on purpose: Vue must not wrap this promise.
        this.pendingRelease = Promise.resolve();
    },
    mounted() {
        EventBus.on("throwErrorForRequiredInputs", this.handleErrorState);
        this.loadFromLocalStorage();
    },
    beforeUnmount() {
        EventBus.off("throwErrorForRequiredInputs", this.handleErrorState);
    },
    watch: {
        required(newValue) {
            this.internalRequired = newValue;
            this.saveToLocalStorage();
        },
        "pageSettings.storageKey"(storageKey) {
            // The upload may mount or finish before the Builder page settings arrive.
            if (!storageKey) return;
            if (this.uploadedImageUrl) this.storeUploadedImage();
            this.loadFromLocalStorage();
        },
        selectedFiles() {
            if (!this.selectedFiles) return;

            this.loading = true;
            this.isError = false;
            this.uploadErrorMessage = "";
            // A previous upload can still fail in the short delay before this
            // selection starts; its error box must not stay next to the new photo.
            EventBus.emit("hideMessageBox", "autoprofile2-error-message");
            // The previous photo must not show while the new one is processed.
            this.imagePreview = undefined;

            // A newer selection supersedes this one; its late result must not
            // overwrite the photo the customer sees now.
            const attempt = ++this.uploadAttempt;
            const isCurrent = () => attempt === this.uploadAttempt;
            let uploadFailed = false;
            const originalFile = this.selectedFiles[0];
            // One full-size decode at a time: start only after the previous
            // selection has let go of its photo, and skip a selection that was
            // replaced while it waited.
            const previousRelease = this.pendingRelease;
            let markReleased;
            this.pendingRelease = new Promise((resolve) => { markReleased = resolve; });
            const preparation = waitForRelease(previousRelease)
                .then(() => (isCurrent() ? prepareImageForUpload(originalFile) : null));
            preparation.then((result) => result?.released).then(markReleased, markReleased);
            preparation
            .then((result) => {
                if (!result || !isCurrent()) return null;
                const { file, prepared, reason, width, height, released, diagnostics } = result;
                if (result.uploadable === false) {
                    // No upload can succeed with this file, so the customer is told right
                    // away. Without a request, installAxiosReporting has nothing to report.
                    captureCustomizerMessage("customizer_photo_upload_blocked", {
                        level: "warning",
                        tags: {
                            component: "AutoProfileAI2",
                            upload_validation_error: result.uploadValidationError,
                            upload_prepare_reason: reason,
                        },
                        extra: {
                            ...diagnostics,
                            original_type: originalFile.type || "unknown",
                            original_bytes: originalFile.size ?? null,
                        },
                    });
                    throw Object.assign(new Error("photo_upload_blocked"), {
                        uploadValidationError: result.uploadValidationError,
                    });
                }
                // Preview the prepared file, and only once the preparation let go of
                // the original, so a huge photo is never decoded twice at once.
                Promise.resolve(released).then(() => {
                    if (isCurrent() && !uploadFailed && !this.imagePreview) {
                        this.showImagePreview(file, width, height);
                    }
                });
                const formData = new FormData();
                formData.append("file", file);
                return axios.post(this.webhookUrl, formData, {
                    customizerFeature: "photo_upload",
                    customizerDiagnostics: {
                        upload_prepared: prepared,
                        upload_prepare_reason: reason,
                        original_type: originalFile.type || "unknown",
                        original_bytes: originalFile.size ?? null,
                        upload_bytes: file.size ?? null,
                        ...diagnostics,
                    },
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
            })
            .then(response => {
                if (!response || !isCurrent()) return;
                if (response.status === 200 && response.data && response.data.url) {
                    this.uploadedImageUrl = response.data.url;
                    this.storeUploadedImage();
                    // Still waiting for a slow local preview: show the uploaded photo.
                    if (!this.imagePreview) this.showImagePreview(this.uploadedImageUrl);
                } else {
                    throw new Error("Webhook response was not successful or did not contain a URL");
                }
            })
            .catch(error => {
                // sentry-ignore: a failed upload is reported by installAxiosReporting (feature photo_upload), a blocked one above
                if (!isCurrent()) return;
                uploadFailed = true;
                if (process.env.VUE_APP_DEV_MODE === "true") {
                    console.error("[AutoProfileAI2] Error during image upload", {
                        code: error?.code || "unknown",
                        status: error?.response?.status || "none",
                    });
                }

                // The upload workflow, or the preparation before it, says why the file
                // cannot be used; trying the same file again would fail again, so tell
                // the customer what to change.
                const reason = error?.uploadValidationError || error?.response?.data?.uploadValidationError;
                this.uploadErrorMessage = reason === "file_too_large"
                    ? this.errorMessageTooLarge
                    : reason ? this.errorMessageInvalidFile : "";

                this.selectedFiles = undefined;
                this.imagePreview = undefined;
                EventBus.emit("showMessageBox", "autoprofile2-error-message");
                this.isError = true;
            })
            .finally(() => {
                if (isCurrent()) this.loading = false;
            });
        },
    },
    methods: {
        selectFile() {
            this.selectedFiles = undefined;
            EventBus.emit("hideMessageBox", "autoprofile2-error-message");
            this.isError = false;
            setTimeout(() => {
                if (this.$refs.file && this.$refs.file.files.length > 0) {
                    this.selectedFiles = this.$refs.file.files;
                }
            }, 50);
        },
        openImageUpload() {
            if (window.innerWidth > 991) {
                this.$refs.AutoProfileAI2Popup.openPopup();
            } else {
                this.$refs.AutoProfileAI2Bottomsheet.openBottomsheet();
            }
        },
        handleErrorState(newState) {
            if (newState === false) {
                this.isError = false;
                return;
            }

            if (this.internalRequired && !this.hasImage) {
                this.isError = true;
            }
        },
        showImagePreview(source, width, height) {
            this.imagePreview = typeof source === "string" ? source : URL.createObjectURL(source);
            if (width && height) {
                this.imageAspectRatio = width / height;
                return;
            }
            const previewSource = this.imagePreview;
            const img = new Image();
            img.onload = () => {
                // A newer preview may be showing by now; its layout must not get this photo's ratio.
                if (this.imagePreview === previewSource) {
                    this.imageAspectRatio = img.width / img.height;
                }
            };
            img.src = previewSource;
        },
        storeUploadedImage() {
            const storageKey = this.sharePayload
                ? "sharedCustomizerValues"
                : (this.pageSettings && this.pageSettings.storageKey) || "";

            recordImageUpload(storageKey);

            if (!storageKey) {
                // A generic fallback key is never read by the steps, so the photo
                // would be lost. The storageKey watcher stores it once the key arrives.
                captureCustomizerMessage("customizer_image_upload_storage_key_missing", {
                    level: "warning",
                    tags: { component: "AutoProfileAI2" },
                });
                return;
            }

            const existingData = safeGetJson(storageKey, {});
            const parts = this.imagePayload.split('.');
            let current = existingData;

            for (let i = 0; i < parts.length - 1; i++) {
                if (!current[parts[i]]) current[parts[i]] = {};
                current = current[parts[i]];
            }

            current[parts[parts.length - 1]] = {
                value: this.uploadedImageUrl,
                required: this.internalRequired
            };
            safeSetJson(storageKey, existingData);
        },
        setRequired(newState) {
            this.internalRequired = newState;
            this.saveToLocalStorage();
        },
        loadFromLocalStorage() {
            if (!this.imagePayload) {
                return;
            }
            
            if (!this.pageSettings || !this.pageSettings.storageKey) {
                return;
            }

            const storageKey = this.sharePayload 
                ? "sharedCustomizerValues" 
                : this.pageSettings.storageKey;
                
            const stepValues = safeGetJson(storageKey, {});
            const parts = this.imagePayload.split('.');
            let current = stepValues;
            
            for (let i = 0; i < parts.length - 1; i++) {
                if (!current[parts[i]]) {
                    current[parts[i]] = {};
                }
                current = current[parts[i]];
            }
            
            if (!current[parts[parts.length - 1]]) {
                current[parts[parts.length - 1]] = {
                    value: "",
                    required: this.internalRequired
                };
                
                safeSetJson(storageKey, stepValues);
            } else {
                if (current[parts[parts.length - 1]].value) {
                    this.loading = true;
                    
                    this.imagePreview = current[parts[parts.length - 1]].value;
                    
                    nextTick(() => {
                        const img = new Image();
                        img.onload = () => {
                            this.imageAspectRatio = img.width / img.height;
                            this.loading = false;
                        };
                        img.src = this.imagePreview;
                    });
                }
                
                if (this.required && current[parts[parts.length - 1]].required === false) {
                    // A placeholder stored while the photo was optional must not outvote the
                    // Builder setting: with required:false the step guards let the customer
                    // reach the preview without a photo.
                    current[parts[parts.length - 1]].required = true;
                    this.internalRequired = true;
                    safeSetJson(storageKey, stepValues);
                } else if (current[parts[parts.length - 1]].required !== undefined) {
                    this.internalRequired = current[parts[parts.length - 1]].required;
                } else {
                    current[parts[parts.length - 1]].required = this.internalRequired;
                    safeSetJson(storageKey, stepValues);
                }
            }
        },
        saveToLocalStorage() {
            if (!this.imagePayload) {
                return;
            }
            
            if (!this.pageSettings || !this.pageSettings.storageKey) {
                return;
            }
            
            const storageKey = this.sharePayload 
                ? "sharedCustomizerValues" 
                : this.pageSettings.storageKey;
                
            const stepValues = safeGetJson(storageKey, {});
            const parts = this.imagePayload.split('.');
            let current = stepValues;
            
            for (let i = 0; i < parts.length - 1; i++) {
                if (!current[parts[i]]) {
                    current[parts[i]] = {};
                }
                current = current[parts[i]];
            }
            
            if (!current[parts[parts.length - 1]]) {
                current[parts[parts.length - 1]] = {};
            }
            
            // imagePreview is only a local blob: URL right after an upload.
            current[parts[parts.length - 1]].value = this.hasImage
                ? this.uploadedImageUrl || this.imagePreview
                : "";
            current[parts[parts.length - 1]].required = this.internalRequired;
            
            safeSetJson(storageKey, stepValues);
        }
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.vars.scss" as *;


%flex-center {
    display: flex;
    justify-content: center;
    align-items: center;
}

%flex-column {
    display: flex;
    flex-direction: column;
}


.autoprofile2 {
    display: flex;
    align-items: flex-start;
    width: 100%;
    
    &.has-landscape {
        width: auto;
    }
}


.image-upload {
    @extend %flex-column;
    row-gap: 16px;
    width: 100%;
    max-width: 142px;
    
    &.has-landscape {
        max-width: none;
        width: auto;
    }
}


.image-preview-wrapper {
    @extend %flex-center;
    flex-direction: column;
    width: 142px;
    height: 142px;
    min-width: 142px;
    min-height: 142px;
    border-radius: 16px;
    border: 2px solid $color-new-black;
    cursor: pointer;
    overflow: hidden;
    
    &.error {
        border-color: $color-input-error;
    }
    
    .image-preview-placeholder {
        @extend %flex-center;
        flex-direction: column;
        row-gap: 12px;
        width: 100%;
        padding: 10px;
        text-align: center;
        font-weight: 500;
        color: #6f6c90;
    }
    

    .loading-container {
        @extend %flex-center;
        width: 100%;
        height: 100%;
    }
    

    &.has-image {
        &.is-portrait {
            height: auto;
            width: 142px;
            min-height: 142px;
            
            > img {
                width: 100%;
                height: auto;
                max-height: 300px; 
                object-fit: contain;
            }
        }
        
        &.is-landscape {
            width: auto;
            height: 142px;
            min-width: 142px;
            max-width: 300px; 
            
            > img {
                width: auto;
                height: 100%;
                max-width: 300px; 
                object-fit: contain;
            }
        }
        
        &.is-square {
            width: 142px;
            height: 142px;
            
            > img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }
    }
    

    > img {
        max-width: 100%;
        max-height: 100%;
        object-position: center;
    }
}
</style>
